import { useState, type Dispatch, type SetStateAction } from 'react';
import { useQueryClient, type QueryClient } from '@tanstack/react-query';
import type { TiptapDoc } from '@odo/shared-ui';
import type { SeverityKey } from '../../../patient-files/notes/severity';
import { useUpdateCommunication } from '../../../../hooks/useCommunications';
import { COMMUNICATION_DETAIL_KEY, PATIENT_NOTES_KEY } from '../../../../hooks/queryKeys';
import { createNote } from '../../../../services/patientNotes';
import type {
    CommunicationEntry,
    CommunicationPatientRef
} from '../../../../services/communications';
import { useToast } from '../../../../providers/ToastProvider';

interface UseCreatePatientNotesArgs {
    entry: CommunicationEntry;
    communicationId: string;
    linkedPatients: CommunicationPatientRef[];
}

export interface CreatePatientNotesFields {
    severity: SeverityKey;
    title: string;
    doc: TiptapDoc;
}

export interface UseCreatePatientNotesResult {
    selectedPatients: CommunicationPatientRef[];
    setSelectedPatients: Dispatch<SetStateAction<CommunicationPatientRef[]>>;
    /** The pristine, pre-seeded selection - for the caller's own dirty check. */
    initialSelectedPatients: CommunicationPatientRef[];
    alreadyNotedPatients: CommunicationPatientRef[];
    alreadyNotedIds: Set<string>;
    isSaving: boolean;
    /** Link, then fan out notes, then toast. Resolves true only once every
     *  selected patient's note was created - the caller closes the modal on
     *  true and leaves it open (with only the failed patients selected) on false. */
    handleSave: (fields: CreatePatientNotesFields) => Promise<boolean>;
}

/** The thread link is a convenience, not a prerequisite for the notes
 *  themselves - a failure here only changes which toast fires afterward, it
 *  never blocks note creation. */
async function linkNewPatients(
    updateCommunication: ReturnType<typeof useUpdateCommunication>,
    communicationId: string,
    linkedPatients: CommunicationPatientRef[],
    newlyLinked: CommunicationPatientRef[]
): Promise<boolean> {
    if (newlyLinked.length === 0) return true;
    try {
        await updateCommunication.mutateAsync({
            id: communicationId,
            patch: {
                patientIds: [...linkedPatients.map((p) => p.id), ...newlyLinked.map((p) => p.id)]
            }
        });
        return true;
    } catch {
        return false;
    }
}

interface FanOutResult {
    succeeded: CommunicationPatientRef[];
    failed: CommunicationPatientRef[];
}

/** One POST per patient; invalidates that patient's own notes cache on success. */
async function createNotesForPatients(
    patients: CommunicationPatientRef[],
    entryId: string,
    fields: CreatePatientNotesFields,
    queryClient: QueryClient
): Promise<FanOutResult> {
    const trimmedTitle = fields.title.trim();
    const results = await Promise.allSettled(
        patients.map((patient) =>
            createNote(patient.id, {
                severity: fields.severity,
                title: trimmedTitle,
                bodyDoc: fields.doc,
                communicationEntryId: entryId
            })
        )
    );

    const succeeded: CommunicationPatientRef[] = [];
    const failed: CommunicationPatientRef[] = [];
    results.forEach((result, index) => {
        const patient = patients[index];
        if (result.status === 'fulfilled') {
            succeeded.push(patient);
            queryClient.invalidateQueries({ queryKey: PATIENT_NOTES_KEY(patient.id) });
        } else {
            failed.push(patient);
        }
    });
    return { succeeded, failed };
}

/** One toast, not two: a plain "Note added" next to a raw API error reads as
 *  a random unrelated failure, not as "this one part of what you asked for
 *  didn't happen." */
function showSaveResultToast(
    showToast: ReturnType<typeof useToast>['showToast'],
    { succeeded, failed }: FanOutResult,
    linkFailed: boolean
): void {
    if (succeeded.length > 0) {
        if (linkFailed) {
            showToast(
                succeeded.length === 1
                    ? 'Note created, but linking to the communication failed'
                    : 'Notes created, but linking to the communication failed',
                'error'
            );
        } else {
            showToast(
                succeeded.length === 1
                    ? 'Note added'
                    : `Notes added for ${succeeded.length} patients`,
                'success'
            );
        }
    }
    if (failed.length > 0) {
        showToast(`Failed to add note for ${failed.map((p) => p.name).join(', ')}`, 'error');
    }
}

/**
 * Owns the "Add patient note" modal's save orchestration - auto-link any
 * newly-picked patient to the thread, fan out one note POST per selected
 * patient, then toast - plus the patient-selection state that orchestration
 * needs to react to: a partial failure moves the succeeded patients into
 * `alreadyNotedPatients` immediately, so a retry can't double-create a note
 * for someone who already got one.
 */
export function useCreatePatientNotes({
    entry,
    communicationId,
    linkedPatients
}: UseCreatePatientNotesArgs): UseCreatePatientNotesResult {
    const { showToast } = useToast();
    const queryClient = useQueryClient();
    const updateCommunication = useUpdateCommunication();

    // Grows whenever a save succeeds for a patient (see handleSave) - never
    // shrinks, so it stays correct across retries within one open modal.
    const [extraNotedPatients, setExtraNotedPatients] = useState<CommunicationPatientRef[]>([]);

    const wireNotedIds = new Set((entry.patientNotes ?? []).map((n) => n.patientId));
    const alreadyNotedPatients = [
        ...linkedPatients.filter((p) => wireNotedIds.has(p.id)),
        ...extraNotedPatients
    ];
    const alreadyNotedIds = new Set(alreadyNotedPatients.map((p) => p.id));
    const initialSelectedPatients = linkedPatients.filter((p) => !alreadyNotedIds.has(p.id));

    const [selectedPatients, setSelectedPatients] =
        useState<CommunicationPatientRef[]>(initialSelectedPatients);
    const [isSaving, setIsSaving] = useState(false);

    const handleSave = async (fields: CreatePatientNotesFields): Promise<boolean> => {
        setIsSaving(true);
        try {
            const existingIds = new Set(linkedPatients.map((p) => p.id));
            const newlyLinked = selectedPatients.filter((p) => !existingIds.has(p.id));
            const linkFailed = !(await linkNewPatients(
                updateCommunication,
                communicationId,
                linkedPatients,
                newlyLinked
            ));

            const fanOut = await createNotesForPatients(
                selectedPatients,
                entry.id,
                fields,
                queryClient
            );

            if (fanOut.succeeded.length > 0) {
                queryClient.invalidateQueries({
                    queryKey: COMMUNICATION_DETAIL_KEY(communicationId)
                });
                setExtraNotedPatients((prev) => [...prev, ...fanOut.succeeded]);
            }
            showSaveResultToast(showToast, fanOut, linkFailed);

            if (fanOut.failed.length > 0) {
                // Only the failed patients remain selected, so a retry only
                // targets them - the succeeded ones are already in
                // alreadyNotedPatients above.
                setSelectedPatients(fanOut.failed);
                return false;
            }
            return true;
        } finally {
            setIsSaving(false);
        }
    };

    return {
        selectedPatients,
        setSelectedPatients,
        initialSelectedPatients,
        alreadyNotedPatients,
        alreadyNotedIds,
        isSaving,
        handleSave
    };
}
